/**
 * Safely formats a date, handling both string and Date inputs
 * @param date - Date object or string to format
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date string or 'Invalid Date' if date is invalid
 */
export const formatDate = (
  date: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }
): string => {
  try {
    // Handle null/undefined
    if (!date) {
      return 'N/A';
    }

    // Convert string to Date if needed
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      console.warn('Invalid date provided to formatDate:', date);
      return 'Invalid Date';
    }
    
    return new Intl.DateTimeFormat('en-US', options).format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', error, date);
    return 'Invalid Date';
  }
};

/**
 * Formats a date with time
 */
export const formatDateTime = (
  date: Date | string | null | undefined
): string => {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formats a date for display in tables
 */
export const formatTableDate = (
  date: Date | string | null | undefined
): string => {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Formats a date for display in cards
 */
export const formatCardDate = (
  date: Date | string | null | undefined
): string => {
  return formatDate(date, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Checks if a date is valid
 */
export const isValidDate = (date: Date | string | null | undefined): boolean => {
  try {
    if (!date) return false;
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return !isNaN(dateObj.getTime());
  } catch {
    return false;
  }
}; 